/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const viVN: LocalizationResource = {
  locale: 'vi-VN',
  apiKeys: {
    action__add: 'Thêm khoá mới',
    action__search: 'Tìm kiếm khoá',
    copySecret: {
      formButtonPrimary__copyAndClose: 'Sao chép và đóng',
      formHint: 'Vì lý do bảo mật, chúng tôi sẽ không cho phép bạn xem lại sau này.',
      formTitle: 'Sao chép khóa API "{{name}}" của bạn ngay bây giờ',
    },
    createdAndExpirationStatus__expiresOn:
      "Tạo {{ createdDate | shortDate('vi-VN') }} • Hết hạn {{ expiresDate | longDate('vi-VN') }}",
    createdAndExpirationStatus__never: "Tạo {{ createdDate | shortDate('vi-VN') }} • Không hết hạn",
    detailsTitle__emptyRow: 'Không tìm thấy khoá API',
    formButtonPrimary__add: 'Tạo khoá',
    formFieldCaption__expiration__expiresOn: 'Hết hạn {{ date }}',
    formFieldCaption__expiration__never: 'Khoá này sẽ không hết hạn',
    formFieldOption__expiration__180d: '180 Ngày',
    formFieldOption__expiration__1d: '1 Ngày',
    formFieldOption__expiration__1y: '1 Năm',
    formFieldOption__expiration__30d: '30 Ngày',
    formFieldOption__expiration__60d: '60 Ngày',
    formFieldOption__expiration__7d: '7 Ngày',
    formFieldOption__expiration__90d: '90 Ngày',
    formFieldOption__expiration__never: 'Không hết hạn',
    formHint: 'Nhập tên để tạo khoá mới. Bạn sẽ có thể hủy bỏ bất kỳ lúc nào.',
    formTitle: 'Thêm khoá API mới',
    lastUsed__days: '{{days}} ngày trước',
    lastUsed__hours: '{{hours}} giờ trước',
    lastUsed__minutes: '{{minutes}} phút trước',
    lastUsed__months: '{{months}} tháng trước',
    lastUsed__seconds: '{{seconds}} giây trước',
    lastUsed__years: '{{years}} năm trước',
    menuAction__revoke: 'Hủy khoá',
    revokeConfirmation: {
      confirmationText: 'Hủy',
      formButtonPrimary__revoke: 'Hủy khoá',
      formHint: 'Bạn có chắc chắn muốn xóa khoá này không?',
      formTitle: 'Hủy khoá "{{apiKeyName}}" không?',
      inputLabel: undefined,
    },
    tableHeader__actions: undefined,
    tableHeader__lastUsed: undefined,
    tableHeader__name: undefined,
  },
  backButton: 'Quay lại',
  badge__activePlan: 'Đang hoạt động',
  badge__banned: undefined,
  badge__canceledEndsAt: "Đã hủy • Kết thúc {{ date | shortDate('vi-VN') }}",
  badge__currentPlan: 'Gói hiện tại',
  badge__default: 'Mặc định',
  badge__endsAt: "Kết thúc {{ date | shortDate('vi-VN') }}",
  badge__expired: 'Đã hết hạn',
  badge__freeTrial: 'Dùng thử miễn phí',
  badge__otherImpersonatorDevice: 'Thiết bị giả mạo khác',
  badge__pastDueAt: "Quá hạn {{ date | shortDate('vi-VN') }}",
  badge__pastDuePlan: 'Quá hạn',
  badge__primary: 'Chính',
  badge__renewsAt: "Gia hạn {{ date | shortDate('vi-VN') }}",
  badge__requiresAction: 'Yêu cầu hành động',
  badge__startsAt: "Bắt đầu {{ date | shortDate('vi-VN') }}",
  badge__thisDevice: 'Thiết bị này',
  badge__trialEndsAt: "Kết thúc dùng thử {{ date | shortDate('vi-VN') }}",
  badge__unverified: 'Chưa xác minh',
  badge__upcomingPlan: 'Sắp tới',
  badge__userDevice: 'Thiết bị người dùng',
  badge__you: 'Bạn',
  billing: {
    accountCredit: undefined,
    addPaymentMethod__label: 'Thêm phương thức thanh toán',
    alwaysFree: 'Miễn phí mãi mãi',
    annually: 'Hàng năm',
    availableFeatures: 'Tính năng có sẵn',
    billedAnnually: 'Tính phí hàng năm',
    billedAnnuallyOnly: undefined,
    billedMonthly: undefined,
    billedMonthlyOnly: 'Chỉ tính phí hàng tháng',
    cancelFreeTrial: 'Hủy dùng thử miễn phí',
    cancelFreeTrialAccessUntil:
      "Bản dùng thử của bạn sẽ vẫn hoạt động cho đến {{ date | longDate('vi-VN') }}. Sau đó, bạn sẽ mất quyền truy cập vào các tính năng dùng thử. Bạn sẽ không bị tính phí.",
    cancelFreeTrialTitle: 'Hủy dùng thử miễn phí cho gói {{plan}}?',
    cancelSubscription: 'Hủy đăng ký',
    cancelSubscriptionAccessUntil:
      "Bạn có thể tiếp tục sử dụng tính năng '{{plan}}' cho đến {{ date | longDate('vi-VN') }}, sau đó bạn sẽ không còn quyền truy cập.",
    cancelSubscriptionNoCharge: 'Bạn sẽ không bị tính phí cho đăng ký này.',
    cancelSubscriptionPastDue:
      'Gói đăng ký của bạn sẽ kết thúc ngay lập tức và bạn sẽ mất quyền truy cập vào tất cả các tính năng của gói. Bạn sẽ được yêu cầu thanh toán số tiền quá hạn trong lần đăng ký tiếp theo.',
    cancelSubscriptionTitle: 'Hủy đăng ký {{plan}}?',
    cannotSubscribeMonthly:
      'Bạn không thể đăng ký gói này bằng cách thanh toán hàng tháng. Để đăng ký gói này, bạn cần chọn thanh toán hàng năm.',
    cannotSubscribeUnrecoverable: 'Bạn không thể đăng ký gói này. Gói đăng ký hiện tại của bạn đắt hơn gói này.',
    checkout: {
      description__paymentSuccessful: 'Thanh toán của bạn đã thành công.',
      description__subscriptionSuccessful: 'Đăng ký mới của bạn đã được thiết lập.',
      downgradeNotice:
        'Bạn sẽ giữ đăng ký hiện tại và các tính năng của nó cho đến cuối chu kỳ thanh toán, sau đó bạn sẽ được chuyển sang đăng ký này.',
      emailForm: {
        subtitle: 'Trước khi bạn có thể hoàn thành việc mua hàng, bạn phải thêm địa chỉ email nơi gửi hóa đơn.',
        title: 'Thêm địa chỉ email',
      },
      lineItems: {
        title__freeTrialEndsAt: 'Dùng thử kết thúc vào',
        title__paymentMethod: 'Phương thức thanh toán',
        title__statementId: 'ID hóa đơn',
        title__subscriptionBegins: 'Đăng ký bắt đầu',
        title__totalPaid: 'Tổng thanh toán',
      },
      pastDueNotice: 'Đăng ký trước của bạn đã quá hạn và chưa thanh toán.',
      perMonth: 'hàng tháng',
      title: 'Thanh toán',
      title__paymentSuccessful: 'Thanh toán thành công!',
      title__subscriptionSuccessful: 'Thành công!',
      title__trialSuccess: 'Đã bắt đầu dùng thử thành công!',
      totalDueAfterTrial: 'Tổng số tiền phải trả sau khi dùng thử kết thúc trong {{days}} ngày',
      totalDuePerPeriod: undefined,
    },
    credit: 'Tín dụng',
    creditRemainder: 'Tín dụng cho phần còn lại của đăng ký hiện tại.',
    defaultFreePlanActive: 'Bạn hiện đang trên gói Miễn phí',
    free: 'Miễn phí',
    getStarted: 'Bắt đầu',
    highlightedPlanBadge: 'Phổ biến',
    keepFreeTrial: 'Giữ bản dùng thử miễn phí',
    keepSubscription: 'Giữ đăng ký',
    manage: 'Quản lý',
    manageSubscription: 'Quản lý đăng ký',
    month: 'Tháng',
    monthAbbreviation: undefined,
    monthPerUnit: undefined,
    monthly: 'Hàng tháng',
    pastDue: 'Quá hạn',
    pay: 'Thanh toán {{amount}}',
    payerCreditRemainder: undefined,
    paymentMethod: {
      applePayDescription: {
        annual: 'Thanh toán hàng năm',
        monthly: 'Thanh toán hàng tháng',
      },
      dev: {
        anyNumbers: 'Bất kỳ số nào',
        cardNumber: 'Số thẻ',
        cvcZip: 'CVC, ZIP',
        developmentMode: 'Chế độ phát triển',
        expirationDate: 'Ngày hết hạn',
        testCardInfo: 'Thông tin thử nghiệm',
      },
    },
    paymentMethods__label: 'Phương thức thanh toán',
    pricingTable: {
      billingCycle: 'Chu kỳ thanh toán',
      included: 'Bao gồm',
      seatCost: {
        additionalSeats: undefined,
        freeUpToSeats: undefined,
        includedSeats: undefined,
        perSeat: undefined,
        tooltip: {
          additionalSeatsEach: undefined,
          firstSeatsIncludedInPlan: undefined,
          freeForUpToSeats: undefined,
        },
        unlimitedSeats: undefined,
        upToSeats: undefined,
      },
    },
    proratedDiscount: undefined,
    prorationCredit: undefined,
    reSubscribe: 'Đăng ký lại',
    seatBreakdownIncludedPlural: undefined,
    seatBreakdownIncludedSingular: undefined,
    seatBreakdownPlural: undefined,
    seatBreakdownSingular: undefined,
    seats: undefined,
    seatsWithLimit: undefined,
    seeAllFeatures: 'Xem tất cả tính năng',
    startFreeTrial: 'Bắt đầu dùng thử miễn phí',
    startFreeTrial__days: 'Bắt đầu dùng thử miễn phí {{days}} ngày',
    subscribe: 'Đăng ký',
    subscriptionDetails: {
      beginsOn: 'Bắt đầu vào',
      currentBillingCycle: 'Chu kỳ thanh toán hiện tại',
      endsOn: 'Kết thúc vào',
      firstPaymentAmount: 'Số tiền thanh toán đầu tiên',
      firstPaymentOn: 'Thanh toán đầu tiên vào',
      nextPaymentAmount: 'Số tiền thanh toán tiếp theo',
      nextPaymentOn: 'Thanh toán tiếp theo vào',
      pastDueAt: 'Quá hạn vào',
      renewsAt: 'Gia hạn vào',
      subscribedOn: 'Đăng ký vào',
      title: 'Gói đăng ký',
      trialEndsOn: 'Dùng thử kết thúc vào',
      trialStartedOn: 'Dùng thử bắt đầu vào',
    },
    subtotal: 'Tổng cộng',
    subtotalRenewal: undefined,
    switchPlan: 'Chuyển sang gói này',
    switchToAnnual: 'Chuyển sang hàng năm',
    switchToAnnualWithAnnualPrice: 'Chuyển sang gói năm {{price}} / năm',
    switchToMonthly: 'Chuyển sang hàng tháng',
    switchToMonthlyWithPrice: 'Chuyển sang gói tháng {{price}} / tháng',
    totalDue: 'Tổng cần thanh toán',
    totalDuePerPeriod: undefined,
    totalDueToday: 'Tổng cần thanh toán hôm nay',
    viewFeatures: 'Xem tính năng',
    viewPayment: 'Xem thanh toán',
    year: 'Năm',
    yearAbbreviation: undefined,
    yearPerUnit: undefined,
  },
  configureSSO: {
    activate: {
      activateButton: undefined,
      activeSubtitle: undefined,
      activeTitle: undefined,
      doneButton: undefined,
      skipButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    changeProviderDialog: {
      cancelButton: undefined,
      confirmButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    configureStep: {
      activeConnectionWarning: {
        dismiss: undefined,
        title: undefined,
      },
      attributeMappingTable: {
        badges: {
          optional: undefined,
          required: undefined,
        },
      },
      samlCustom: {
        assignUsersStep: {
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attributeName: undefined,
              userAttribute: undefined,
            },
            rows: {
              email: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              firstName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
              lastName: {
                attributeName: undefined,
                userAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            paragraph: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
      samlGoogle: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              appAttribute: undefined,
              googleAttribute: undefined,
            },
            rows: {
              email: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              firstName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              lastName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        configureUserAccess: {
          assignUsersInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          headerSubtitle: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataFile: {
            description: undefined,
            fileUploaded: undefined,
            label: undefined,
            removeFile: undefined,
            replaceFile: undefined,
            uploadFile: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataFile: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          nameIdInstructions: {
            step1: undefined,
            step2: undefined,
          },
          paragraph: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          title: undefined,
        },
      },
      samlMicrosoft: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attribute: undefined,
              claimName: undefined,
              value: undefined,
            },
            copyClaimName: undefined,
            copyClaimNameCopied: undefined,
            rows: {
              email: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              firstName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              lastName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          step1: undefined,
          step2: undefined,
          title: undefined,
        },
        createAppStep: {
          assignUsersInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: {
              label: undefined,
              subSteps: {
                appName: undefined,
                create: undefined,
                nonGallery: undefined,
              },
            },
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          step1: undefined,
          step2: undefined,
          step3: undefined,
          step4: undefined,
          step5: undefined,
          step6: undefined,
          title: undefined,
        },
      },
      samlOkta: {
        assignUsersStep: {
          assignUsersInstructions: {
            paragraph: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
          },
          headerSubtitle: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              expression: undefined,
              name: undefined,
            },
            rows: {
              email: {
                expression: undefined,
                name: undefined,
              },
              firstName: {
                expression: undefined,
                name: undefined,
              },
              lastName: {
                expression: undefined,
                name: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        createAppStep: {
          completeSamlIntegrationInstructions: {
            step1: undefined,
            step2: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            serviceProviderFields: {
              acsUrl: {
                label: undefined,
              },
              spEntityId: {
                label: undefined,
              },
            },
            title: undefined,
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
    },
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'Liên hệ với quản trị viên tổ chức của bạn để nâng cấp quyền của bạn.',
      title: 'Bạn không có quyền quản lý đăng nhập một lần (SSO)',
    },
    navbar: {
      title: 'Cấu hình đăng nhập một lần (SSO)',
    },
    organizationDomainsStep: {
      domainCard: {
        badge__expired: undefined,
        badge__unverified: 'Chưa xác minh',
        badge__verified: 'Đã xác minh',
        expiredAtLabel: undefined,
        expiredLabel: undefined,
        removeButtonTooltip__lastVerifiedDomain: undefined,
        removeButtonTooltip__lastVerifiedDomainActive: undefined,
        txtRecord: {
          hostLabel: 'Máy chủ / Tên',
          instructions:
            'Thêm bản ghi TXT này vào nhà cung cấp DNS của bạn. Chúng tôi sẽ tự động xác minh khi bản ghi hoạt động.',
          typeLabel: 'Loại',
          valueLabel: 'Giá trị',
        },
        verifiedAtLabel: "Đã xác minh vào {{ date | shortDate('vi-VN') }}",
        verifyAgainButton: undefined,
      },
      domainSuggestion: {
        formButtonPrimary__add: 'Thêm {{domain}}',
        messageLabel: 'Email của bạn sử dụng {{domain}}. Bạn có muốn thêm nó không?',
      },
      formButtonPrimary__add: 'Thêm',
      formFieldInputPlaceholder__domain: 'Thêm tên miền',
      formFieldLabel__domain: 'Tên miền',
      removeDomainDialog: {
        cancelButton: undefined,
        removeButton: undefined,
        subtitle__active: undefined,
        subtitle__inactive: undefined,
        title: undefined,
      },
      subtitle: 'Thêm và xác minh quyền sở hữu các tên miền mà tổ chức của bạn dùng để đăng nhập.',
      title: 'Thêm tên miền SSO',
    },
    resetConnectionDialog: {
      cancelButton: undefined,
      confirmationFieldLabel: undefined,
      confirmationFieldPlaceholder: undefined,
      resetButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    selectProviderStep: {
      saml: {
        customSaml: 'Nhà cung cấp SAML tùy chỉnh',
        google: undefined,
        groupLabel: 'SAML',
        microsoft: undefined,
        okta: 'Okta Workforce',
      },
      subtitle: 'Chọn nhà cung cấp mà bạn sẽ thiết lập SSO cho.',
      title: 'Chọn nhà cung cấp',
      warning: 'Khi đã chọn nhà cung cấp, bạn không thể thay đổi cho đến khi cấu hình hoàn tất',
    },
    testConfigurationStep: {
      error__noSuccessfulTestRun: undefined,
      subtitle: undefined,
      testResults: {
        actionLabel__refresh: undefined,
        empty: {
          subtitle: undefined,
          title: undefined,
        },
        polling: undefined,
        status__failed: undefined,
        status__pending: undefined,
        status__success: undefined,
        title: undefined,
      },
      testRunDetails: {
        howToFix: {
          actionLabel__viewDocumentation: undefined,
          oauth_access_denied: {
            description: undefined,
          },
          oauth_fetch_user_error: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
          },
          oauth_token_exchange_error: {
            description: undefined,
          },
          saml_email_address_domain_mismatch: {
            description: undefined,
          },
          saml_response_relaystate_missing: {
            description: undefined,
          },
          saml_user_attribute_missing: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          sectionTitle: undefined,
        },
        parsedUserInfo: {
          email: undefined,
          firstName: undefined,
          sectionTitle: undefined,
        },
        runDetails: {
          actionLabel__copied: undefined,
          actionLabel__copy: undefined,
          errorCode: undefined,
          fullMessage: undefined,
          sectionTitle: undefined,
          status: undefined,
          timestamp: undefined,
        },
        title: undefined,
      },
      testUrl: {
        actionLabel__open: undefined,
      },
      title: undefined,
    },
  },
  createOrganization: {
    formButtonSubmit: 'Tạo tổ chức',
    invitePage: {
      formButtonReset: 'Bỏ qua',
    },
    title: 'Tạo tổ chức',
  },
  dates: {
    lastDay: "Hôm qua lúc {{ date | timeString('vi-VN') }}",
    next6Days: "{{ date | weekday('vi-VN','long') }} lúc {{ date | timeString('vi-VN') }}",
    nextDay: "Ngày mai lúc {{ date | timeString('vi-VN') }}",
    numeric: "{{ date | numeric('vi-VN') }}",
    previous6Days: "{{ date | weekday('vi-VN','long') }} lúc {{ date | timeString('vi-VN') }}",
    sameDay: "Hôm nay lúc {{ date | timeString('vi-VN') }}",
  },
  dividerText: 'hoặc',
  footerActionLink__alternativePhoneCodeProvider: 'Gửi mã qua SMS thay vì email',
  footerActionLink__useAnotherMethod: 'Sử dụng phương thức khác',
  footerPageLink__help: 'Trợ giúp',
  footerPageLink__privacy: 'Quyền riêng tư',
  footerPageLink__terms: 'Điều khoản',
  formButtonPrimary: 'Tiếp tục',
  formButtonPrimary__verify: 'Xác minh',
  formFieldAction__forgotPassword: 'Quên mật khẩu?',
  formFieldError__matchingPasswords: 'Mật khẩu trùng khớp.',
  formFieldError__notMatchingPasswords: 'Mật khẩu không trùng khớp.',
  formFieldError__verificationLinkExpired: 'Liên kết xác minh đã hết hạn. Vui lòng yêu cầu liên kết mới.',
  formFieldHintText__optional: 'Tùy chọn',
  formFieldHintText__slug:
    'Một slug là một ID dễ đọc bởi con người mà phải độc nhất. Nó thường được sử dụng trong URL.',
  formFieldInputPlaceholder__apiKeyDescription: 'Giải thích tại sao bạn đang tạo khoá này',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'Chọn ngày',
  formFieldInputPlaceholder__apiKeyName: 'Nhập tên khoá bí mật',
  formFieldInputPlaceholder__backupCode: 'Nhập mã dự phòng',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Xóa tài khoản',
  formFieldInputPlaceholder__emailAddress: 'Nhập địa chỉ email của bạn',
  formFieldInputPlaceholder__emailAddress_username: 'Nhập email hoặc tên người dùng',
  formFieldInputPlaceholder__emailAddresses: 'example@email.com, example2@email.com',
  formFieldInputPlaceholder__firstName: 'Tên',
  formFieldInputPlaceholder__lastName: 'Họ',
  formFieldInputPlaceholder__organizationDomain: 'example.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'you@example.com',
  formFieldInputPlaceholder__organizationName: 'Tên tổ chức',
  formFieldInputPlaceholder__organizationSlug: 'my-org',
  formFieldInputPlaceholder__password: 'Nhập mật khẩu của bạn',
  formFieldInputPlaceholder__phoneNumber: 'Nhập số điện thoại của bạn',
  formFieldInputPlaceholder__signUpPassword: undefined,
  formFieldInputPlaceholder__username: 'Nhập tên người dùng của bạn',
  formFieldInput__emailAddress_format: 'Định dạng ví dụ: name@example.com',
  formFieldLabel__apiKey: 'Khóa API',
  formFieldLabel__apiKeyDescription: 'Mô tả',
  formFieldLabel__apiKeyExpiration: 'Hết hạn',
  formFieldLabel__apiKeyName: 'Tên khoá bí mật',
  formFieldLabel__automaticInvitations: 'Cho phép tự động mời cho tên miền này',
  formFieldLabel__backupCode: 'Mã dự phòng',
  formFieldLabel__confirmDeletion: 'Xác nhận',
  formFieldLabel__confirmPassword: 'Xác nhận mật khẩu',
  formFieldLabel__currentPassword: 'Mật khẩu hiện tại',
  formFieldLabel__emailAddress: 'Địa chỉ email',
  formFieldLabel__emailAddress_username: 'Email hoặc tên người dùng',
  formFieldLabel__emailAddresses: 'Địa chỉ email',
  formFieldLabel__firstName: 'Tên',
  formFieldLabel__lastName: 'Họ',
  formFieldLabel__newPassword: 'Mật khẩu mới',
  formFieldLabel__organizationDomain: 'Tên miền',
  formFieldLabel__organizationDomainDeletePending: 'Xóa mời và gợi ý đang chờ',
  formFieldLabel__organizationDomainEmailAddress: 'Địa chỉ email xác minh',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Nhập địa chỉ email dưới tên miền này để nhận mã và xác minh tên miền này.',
  formFieldLabel__organizationName: 'Tên',
  formFieldLabel__organizationSlug: 'Slug',
  formFieldLabel__passkeyName: 'Tên của passkey',
  formFieldLabel__password: 'Mật khẩu',
  formFieldLabel__phoneNumber: 'Số điện thoại',
  formFieldLabel__role: 'Vai trò',
  formFieldLabel__signOutOfOtherSessions: 'Đăng xuất khỏi tất cả thiết bị khác',
  formFieldLabel__username: 'Tên người dùng',
  identityPreviewEditButton__emailAddress: undefined,
  identityPreviewEditButton__identifier: undefined,
  identityPreviewEditButton__phoneNumber: undefined,
  impersonationFab: {
    action__signOut: 'Đăng xuất',
    title: 'Đăng nhập với {{identifier}}',
  },
  lastAuthenticationStrategy: 'Lần sử dụng cuối cùng',
  maintenanceMode: 'Chúng tôi đang trong quá trình bảo trì, nhưng đừng lo lắng, nó không nên mất quá nhiều thời gian.',
  membershipRole__admin: 'Quản trị viên',
  membershipRole__basicMember: 'Thành viên',
  membershipRole__guestMember: 'Khách',
  oauthConsent: {
    action__allow: undefined,
    action__deny: undefined,
    offlineAccessNotice: undefined,
    redirectNotice: undefined,
    redirectUriModal: {
      subtitle: undefined,
      title: undefined,
    },
    scopeList: {
      title: undefined,
    },
    subtitle: undefined,
    viewFullUrl: undefined,
    warning: undefined,
  },
  organizationList: {
    action__createOrganization: 'Tạo tổ chức',
    action__invitationAccept: 'Tham gia',
    action__suggestionsAccept: 'Yêu cầu tham gia',
    createOrganization: 'Tạo tổ chức',
    invitationAcceptedLabel: 'Tham gia',
    subtitle: 'để tiếp tục đến {{applicationName}}',
    suggestionsAcceptedLabel: 'Đang chờ phê duyệt',
    title: 'Chọn tài khoản',
    titleWithoutPersonal: 'Chọn tổ chức',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'Khoá API',
    },
    badge__automaticInvitation: 'Tự động mời',
    badge__automaticSuggestion: 'Tự động gợi ý',
    badge__enterpriseSso: undefined,
    badge__manualInvitation: 'Không tự động đăng ký',
    badge__unverified: 'Chưa xác minh',
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: 'Không có lịch sử thanh toán',
        notFound: 'Không tìm thấy lịch sử thanh toán',
        tableHeader__amount: 'Số tiền',
        tableHeader__date: 'Ngày',
        tableHeader__status: 'Trạng thái',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Làm mặc định',
        actionLabel__remove: 'Xóa',
        add: 'Thêm phương thức thanh toán mới',
        addSubtitle: 'Thêm phương thức thanh toán mới vào tài khoản của bạn.',
        cancelButton: 'Hủy',
        formButtonPrimary__add: 'Thêm phương thức thanh toán',
        formButtonPrimary__pay: 'Thanh toán {{amount}}',
        payWithTestCardButton: 'Thanh toán với thẻ thử nghiệm',
        removeMethod: {
          messageLine1: '{{identifier}} sẽ bị xóa khỏi tài khoản này.',
          messageLine2:
            'Bạn sẽ không còn thể sử dụng nguồn thanh toán này và bất kỳ đăng ký lặp lại nào phụ thuộc vào nó sẽ không còn hoạt động.',
          successMessage: '{{paymentMethod}} đã bị xóa khỏi tài khoản của bạn.',
          title: 'Xóa phương thức thanh toán',
        },
        title: 'Phương thức thanh toán',
      },
      start: {
        headerTitle__payments: 'Thanh toán',
        headerTitle__plans: 'Gói',
        headerTitle__statements: 'Hóa đơn',
        headerTitle__subscriptions: 'Đăng ký',
      },
      statementsSection: {
        empty: 'Không có hóa đơn để hiển thị',
        itemCaption__paidForPlan: 'Thanh toán cho gói {{plan}} {{period}}',
        itemCaption__payerCredit: undefined,
        itemCaption__proratedCredit: 'Tín dụng phân chia cho sử dụng một phần của đăng ký trước',
        itemCaption__subscribedAndPaidForPlan: 'Đăng ký và thanh toán cho gói {{plan}} {{period}}',
        notFound: 'Không tìm thấy hóa đơn',
        tableHeader__amount: 'Số tiền',
        tableHeader__date: 'Ngày',
        title: 'Hóa đơn',
        totalPaid: 'Tổng thanh toán',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Quản lý',
        actionLabel__newSubscription: 'Đăng ký gói',
        actionLabel__switchPlan: 'Chuyển gói',
        includedSeatsUsage: undefined,
        overview: undefined,
        paidSeatsUsage: undefined,
        seatLimit: undefined,
        seatLimitAndIncludedSeats: undefined,
        tableHeader__edit: 'Sửa',
        tableHeader__plan: 'Gói',
        tableHeader__startDate: 'Ngày bắt đầu',
        title: 'Đăng ký',
      },
      subscriptionsSection: {
        actionLabel__default: 'Quản lý',
      },
      switchPlansSection: {
        title: 'Chuyển gói',
      },
      title: 'Thanh toán',
    },
    createDomainPage: {
      subtitle:
        'Thêm tên miền để xác minh. Người dùng với địa chỉ email ở tên miền này có thể tham gia tổ chức tự động hoặc yêu cầu tham gia.',
      title: 'Thêm tên miền',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'Mời không thể được gửi. Đã có mời đang chờ cho các địa chỉ email sau: {{email_addresses}}.',
      formButtonPrimary__continue: 'Gửi mời',
      formButtonPrimary__purchaseSeats: undefined,
      selectDropdown__role: 'Chọn vai trò',
      subtitle: 'Nhập hoặc dán một hoặc nhiều địa chỉ email, cách nhau bằng dấu cách hoặc dấu phẩy.',
      successMessage: 'Mời đã được gửi thành công',
      title: 'Mời thành viên mới',
    },
    membersPage: {
      action__invite: 'Mời',
      action__search: 'Tìm kiếm',
      activeMembersTab: {
        menuAction__remove: 'Xóa thành viên',
        tableHeader__actions: 'Hành động',
        tableHeader__joined: 'Tham gia',
        tableHeader__role: 'Vai trò',
        tableHeader__user: 'Người dùng',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: 'Chúng tôi đang cập nhật các vai trò có sẵn. Sau khi hoàn tất, bạn sẽ có thể cập nhật vai trò lại.',
          title: 'Các vai trò tạm thời bị khóa',
        },
      },
      detailsTitle__emptyRow: 'Không có thành viên để hiển thị',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Mời người dùng bằng cách kết nối tên miền email với tổ chức của bạn. Bất kỳ ai đăng ký với tên miền email khớp sẽ có thể tham gia tổ chức bất cứ lúc nào.',
          headerTitle: 'Tự động mời',
          primaryButton: 'Quản lý tên miền đã xác minh',
        },
        table__emptyRow: 'Không có mời để hiển thị',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Hủy mời',
        tableHeader__invited: 'Đã mời',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Người dùng đăng ký với tên miền email khớp sẽ có thể thấy gợi ý để yêu cầu tham gia tổ chức của bạn.',
          headerTitle: 'Tự động gợi ý',
          primaryButton: 'Quản lý tên miền đã xác minh',
        },
        menuAction__approve: 'Phê duyệt',
        menuAction__reject: 'Từ chối',
        tableHeader__requested: 'Yêu cầu truy cập',
        table__emptyRow: 'Không có yêu cầu để hiển thị',
      },
      start: {
        headerTitle__invitations: 'Mời',
        headerTitle__members: 'Thành viên',
        headerTitle__requests: 'Yêu cầu',
      },
    },
    navbar: {
      apiKeys: 'Khoá API',
      billing: 'Thanh toán',
      description: 'Quản lý tổ chức của bạn.',
      general: 'Tổng quan',
      members: 'Thành viên',
      security: undefined,
      title: 'Tổ chức',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'Bạn không có quyền quản lý thanh toán cho tổ chức này.',
        planMembershipLimitExceeded: undefined,
      },
      title: 'Gói',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Nhập "{{organizationName}}" dưới để tiếp tục.',
          messageLine1: 'Bạn có chắc chắn muốn xóa tổ chức này?',
          messageLine2: 'Hành động này là vĩnh viễn và không thể hoàn tác.',
          successMessage: 'Bạn đã xóa tổ chức.',
          title: 'Xóa tổ chức',
        },
        leaveOrganization: {
          actionDescription: 'Nhập "{{organizationName}}" dưới để tiếp tục.',
          messageLine1:
            'Bạn có chắc chắn muốn rời khỏi tổ chức này? Bạn sẽ mất quyền truy cập vào tổ chức này và ứng dụng của nó.',
          messageLine2: 'Hành động này là vĩnh viễn và không thể hoàn tác.',
          successMessage: 'Bạn đã rời khỏi tổ chức.',
          title: 'Rời khỏi tổ chức',
        },
        title: 'Nguy hiểm',
      },
      domainSection: {
        menuAction__manage: 'Quản lý',
        menuAction__remove: 'Xóa',
        menuAction__verify: 'Xác minh',
        primaryButton: 'Thêm tên miền',
        subtitle:
          'Cho phép người dùng tham gia tổ chức tự động hoặc yêu cầu tham gia dựa trên tên miền email đã xác minh.',
        title: 'Tên miền đã xác minh',
      },
      successMessage: 'Tổ chức đã được cập nhật.',
      title: 'Cập nhật hồ sơ',
    },
    removeDomainPage: {
      messageLine1: 'Tên miền email {{domain}} sẽ bị xóa.',
      messageLine2: 'Người dùng sẽ không thể tham gia tổ chức tự động sau đây.',
      successMessage: '{{domain}} đã bị xóa.',
      title: 'Xóa tên miền',
    },
    securityPage: {
      removeDialog: {
        confirmButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
      ssoSection: {
        badge__active: undefined,
        badge__inProgress: undefined,
        badge__inactive: undefined,
        badge__unconfigured: undefined,
        descriptionLine1: undefined,
        domainLabel: undefined,
        menuAction__activate: undefined,
        menuAction__deactivate: undefined,
        menuAction__edit: undefined,
        menuAction__remove: undefined,
        primaryButton__continueConfiguration: undefined,
        primaryButton__startConfiguration: undefined,
        title: undefined,
        tooltip: undefined,
        tooltipLabel: undefined,
        tooltip__noRole: undefined,
      },
      title: undefined,
    },
    start: {
      headerTitle__general: 'Tổng quan',
      headerTitle__members: 'Thành viên',
      membershipSeatUsageLabel: undefined,
      profileSection: {
        primaryButton: 'Cập nhật hồ sơ',
        title: 'Hồ sơ tổ chức',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Xóa tên miền này sẽ ảnh hưởng đến người dùng đã mời.',
        removeDomainActionLabel__remove: 'Xóa tên miền',
        removeDomainSubtitle: 'Xóa tên miền này khỏi tên miền đã xác minh',
        removeDomainTitle: 'Xóa tên miền',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Người dùng sẽ tự động được mời tham gia tổ chức khi đăng ký và có thể tham gia bất cứ lúc nào.',
        automaticInvitationOption__label: 'Tự động mời',
        automaticSuggestionOption__description:
          'Người dùng sẽ nhận được gợi ý để yêu cầu tham gia, nhưng phải được phê duyệt bởi quản trị viên trước khi họ có thể tham gia tổ chức.',
        automaticSuggestionOption__label: 'Tự động gợi ý',
        calloutInfoLabel: 'Thay đổi chế độ đăng ký sẽ chỉ ảnh hưởng đến người dùng mới.',
        calloutInvitationCountLabel: 'Mời đang chờ gửi đến người dùng: {{count}}',
        calloutSuggestionCountLabel: 'Gợi ý đang chờ gửi đến người dùng: {{count}}',
        manualInvitationOption__description: 'Người dùng chỉ có thể được mời thủ công đến tổ chức.',
        manualInvitationOption__label: 'Không tự động đăng ký',
        subtitle: 'Chọn cách người dùng từ tên miền này có thể tham gia tổ chức.',
      },
      start: {
        headerTitle__danger: 'Nguy hiểm',
        headerTitle__enrollment: 'Chế độ đăng ký',
      },
      subtitle: 'Tên miền {{domain}} đã được xác minh. Tiếp tục bằng cách chọn chế độ đăng ký.',
      title: 'Cập nhật {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Nhập mã xác minh đã gửi đến địa chỉ email của bạn',
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'Tên miền {{domainName}} cần được xác minh qua email.',
      subtitleVerificationCodeScreen: 'Một mã xác minh đã được gửi đến {{emailAddress}}. Nhập mã để tiếp tục.',
      title: 'Xác minh tên miền',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: 'Đóng menu chuyển đổi tổ chức',
    action__createOrganization: 'Tạo tổ chức',
    action__invitationAccept: 'Tham gia',
    action__manageOrganization: 'Quản lý',
    action__openOrganizationSwitcher: 'Mở menu chuyển đổi tổ chức',
    action__suggestionsAccept: 'Yêu cầu tham gia',
    notSelected: 'Không có tổ chức được chọn',
    personalWorkspace: 'Tài khoản cá nhân',
    suggestionsAcceptedLabel: 'Đang chờ phê duyệt',
  },
  paginationButton__next: 'Tiếp',
  paginationButton__previous: 'Trước',
  paginationRowText__displaying: 'Hiển thị',
  paginationRowText__of: 'của',
  reverification: {
    alternativeMethods: {
      actionLink: 'Liên hệ hỗ trợ',
      actionText: 'Không có bất kỳ phương thức nào?',
      blockButton__backupCode: 'Sử dụng mã dự phòng',
      blockButton__emailCode: 'Email mã đến {{identifier}}',
      blockButton__passkey: 'Đăng nhập với mã passkey',
      blockButton__password: 'Tiếp tục với mật khẩu của bạn',
      blockButton__phoneCode: 'Gửi mã SMS đến {{identifier}}',
      blockButton__totp: 'Sử dụng ứng dụng xác thực',
      getHelp: {
        blockButton__emailSupport: 'Email hỗ trợ',
        content:
          'Nếu bạn gặp vấn đề khi xác minh tài khoản của mình, email chúng tôi và chúng tôi sẽ hỗ trợ bạn khôi phục quyền truy cập sớm nhất có thể.',
        title: 'Liên hệ hỗ trợ',
      },
      subtitle: 'Gặp vấn đề? Bạn có thể sử dụng bất kỳ phương thức nào để xác minh.',
      title: 'Sử dụng phương thức khác',
    },
    backupCodeMfa: {
      subtitle: 'Nhập mã dự phòng bạn nhận được khi thiết lập xác thực hai bước',
      title: 'Nhập mã dự phòng',
    },
    emailCode: {
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'Nhập mã đã gửi đến email của bạn để tiếp tục',
      title: 'Xác minh yêu cầu',
    },
    noAvailableMethods: {
      message: 'Không thể tiếp tục với xác minh. Không có yếu tố xác thực phù hợp được cấu hình',
      subtitle: 'Đã xảy ra lỗi',
      title: 'Không thể xác minh tài khoản của bạn',
    },
    passkey: {
      blockButton__passkey: 'Sử dụng mã passkey',
      subtitle:
        'Sử dụng mã passkey xác minh danh tính của bạn. Thiết bị của bạn có thể yêu cầu vân tay, khuôn mặt hoặc khóa màn hình.',
      title: 'Sử dụng mã passkey',
    },
    password: {
      actionLink: 'Sử dụng phương thức khác',
      subtitle: 'Nhập mật khẩu hiện tại để tiếp tục',
      title: 'Xác minh yêu cầu',
    },
    phoneCode: {
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'Nhập mã đã gửi đến điện thoại của bạn để tiếp tục',
      title: 'Xác minh yêu cầu',
    },
    phoneCodeMfa: {
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'Nhập mã đã gửi đến điện thoại của bạn để tiếp tục',
      title: 'Xác minh yêu cầu',
    },
    totpMfa: {
      formTitle: 'Mã xác minh',
      subtitle: 'Nhập mã đã được tạo bởi ứng dụng xác thực của bạn để tiếp tục',
      title: 'Xác minh yêu cầu',
    },
  },
  searchInput: {
    action__clear: undefined,
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Thêm tài khoản',
      action__signOutAll: 'Đăng xuất khỏi tất cả tài khoản',
      subtitle: 'Chọn tài khoản với đó bạn muốn tiếp tục.',
      title: 'Chọn tài khoản',
    },
    alternativeMethods: {
      actionLink: 'Liên hệ hỗ trợ',
      actionText: 'Không có bất kỳ phương thức nào?',
      blockButton__backupCode: 'Sử dụng mã dự phòng',
      blockButton__emailCode: 'Email mã đến {{identifier}}',
      blockButton__emailLink: 'Email liên kết đến {{identifier}}',
      blockButton__passkey: 'Đăng nhập với mã passkey',
      blockButton__password: 'Đăng nhập với mật khẩu',
      blockButton__phoneCode: 'Gửi mã SMS đến {{identifier}}',
      blockButton__totp: 'Sử dụng ứng dụng xác thực',
      getHelp: {
        blockButton__emailSupport: 'Email hỗ trợ',
        content:
          'Nếu bạn gặp vấn đề khi đăng nhập vào tài khoản của mình, email chúng tôi và chúng tôi sẽ hỗ trợ bạn khôi phục quyền truy cập sớm nhất có thể.',
        title: 'Liên hệ hỗ trợ',
      },
      subtitle: 'Gặp vấn đề? Bạn có thể sử dụng bất kỳ phương thức nào để đăng nhập.',
      title: 'Sử dụng phương thức khác',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'để tiếp tục đến {{applicationName}}',
      title: 'Kiểm tra {{provider}}',
    },
    backupCodeMfa: {
      subtitle: 'Mã dự phòng của bạn là mã bạn nhận được khi thiết lập xác thực hai bước.',
      title: 'Nhập mã dự phòng',
    },
    emailCode: {
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'để tiếp tục đến {{applicationName}}',
      title: 'Kiểm tra email',
    },
    emailCodeMfa: {
      formTitle: 'Kiểm tra email',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'để tiếp tục đến {{applicationName}}',
      title: 'Kiểm tra email',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'Để tiếp tục, mở liên kết xác minh trên thiết bị và trình duyệt từ đó bạn đã khởi động đăng nhập',
        title: 'Liên kết xác minh không hợp lệ cho thiết bị này',
      },
      expired: {
        subtitle: 'Quay lại tab gốc để tiếp tục.',
        title: 'Liên kết xác minh đã hết hạn',
      },
      failed: {
        subtitle: 'Quay lại tab gốc để tiếp tục.',
        title: 'Liên kết xác minh không hợp lệ',
      },
      formSubtitle: 'Sử dụng liên kết xác minh đã gửi đến email của bạn',
      formTitle: 'Liên kết xác minh',
      loading: {
        subtitle: 'Bạn sẽ được chuyển hướng sớm',
        title: 'Đang đăng nhập...',
      },
      resendButton: 'Không nhận được liên kết? Gửi lại',
      subtitle: 'để tiếp tục đến {{applicationName}}',
      title: 'Kiểm tra email',
      unusedTab: {
        title: 'Bạn có thể đóng tab này',
      },
      verified: {
        subtitle: 'Bạn sẽ được chuyển hướng sớm',
        title: 'Đăng nhập thành công',
      },
      verifiedSwitchTab: {
        subtitle: 'Quay lại tab gốc để tiếp tục',
        subtitleNewTab: 'Quay lại tab mới được mở để tiếp tục',
        titleNewTab: 'Đăng nhập trên tab khác',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Sử dụng liên kết xác minh được gửi đến email của bạn',
      resendButton: 'Không nhận được liên kết? Gửi lại',
      subtitle: 'để tiếp tục đến {{applicationName}}',
      title: 'Kiểm tra email của bạn',
    },
    enterpriseConnections: {
      subtitle: 'Chọn tài khoản doanh nghiệp bạn muốn dùng để tiếp tục.',
      title: 'Chọn tài khoản doanh nghiệp của bạn',
    },
    forgotPassword: {
      formTitle: 'Mã xác minh mật khẩu',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'để đặt lại mật khẩu của bạn',
      subtitle_email: 'Đầu tiên, nhập mã đã gửi đến email của bạn',
      subtitle_phone: 'Đầu tiên, nhập mã đã gửi đến điện thoại của bạn',
      title: 'Đặt lại mật khẩu',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Đặt lại mật khẩu',
      label__alternativeMethods: 'Hoặc, đăng nhập với phương thức khác',
      title: 'Quên mật khẩu?',
    },
    newDeviceVerificationNotice:
      'Bạn đang đăng nhập từ thiết bị mới. Chúng tôi yêu cầu xác minh để giữ tài khoản của bạn an toàn.',
    noAvailableMethods: {
      message: 'Không thể tiếp tục đăng nhập. Không có yếu tố xác thực phù hợp được cấu hình',
      subtitle: 'Đã xảy ra lỗi',
      title: 'Không thể đăng nhập',
    },
    passkey: {
      subtitle:
        'Sử dụng mã passkey xác minh danh tính của bạn. Thiết bị của bạn có thể yêu cầu vân tay, khuôn mặt hoặc khóa màn hình.',
      title: 'Sử dụng mã passkey',
    },
    password: {
      actionLink: 'Sử dụng phương thức khác',
      subtitle: 'Nhập mật khẩu được liên kết với tài khoản của bạn',
      title: 'Nhập mật khẩu',
    },
    passwordCompromised: {
      title: 'Mật khẩu đã bị xâm phạm',
    },
    passwordPwned: {
      title: 'Mật khẩu bị rò rỉ',
    },
    passwordUntrusted: {
      title: 'Mật khẩu không đáng tin cậy',
    },
    phoneCode: {
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'để tiếp tục đến {{applicationName}}',
      title: 'Kiểm tra điện thoại',
    },
    phoneCodeMfa: {
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'để tiếp tục đến {{applicationName}}',
      title: 'Kiểm tra điện thoại',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    resetPassword: {
      formButtonPrimary: 'Đặt lại mật khẩu',
      requiredMessage: 'Vì lý do bảo mật, việc đặt lại mật khẩu là bắt buộc.',
      successMessage: 'Mật khẩu của bạn đã được thay đổi thành công. Đang đăng nhập, vui lòng chờ một lát.',
      title: 'Đặt mật khẩu mới',
    },
    resetPasswordMfa: {
      detailsLabel: 'Chúng tôi cần xác minh danh tính của bạn trước khi đặt lại mật khẩu.',
    },
    start: {
      actionLink: 'Đăng ký',
      actionLink__join_waitlist: 'Tham gia danh sách chờ',
      actionLink__use_email: 'Sử dụng email',
      actionLink__use_email_username: 'Sử dụng email hoặc tên người dùng',
      actionLink__use_passkey: 'Sử dụng mã passkey thay vì',
      actionLink__use_phone: 'Sử dụng điện thoại',
      actionLink__use_username: 'Sử dụng tên người dùng',
      actionText: 'Không có tài khoản?',
      actionText__join_waitlist: 'Muốn trải nghiệm sớm?',
      alternativePhoneCodeProvider: {
        actionLink: 'Sử dụng phương thức khác',
        label: '{{provider}} số điện thoại',
        subtitle: 'Nhập số điện thoại của bạn để nhận mã xác minh trên {{provider}}.',
        title: 'Đăng nhập vào {{applicationName}} với {{provider}}',
      },
      subtitle: 'Chào mừng trở lại! Vui lòng đăng nhập để tiếp tục',
      subtitleCombined: undefined,
      title: 'Đăng nhập vào {{applicationName}}',
      titleCombined: 'Tiếp tục đến {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Mã xác minh',
      subtitle: 'Để tiếp tục, vui lòng nhập mã xác minh được tạo bởi ứng dụng xác thực của bạn',
      title: 'Xác thực hai bước',
    },
    web3Solana: {
      subtitle: 'Chọn ví bên dưới để đăng nhập',
      title: 'Đăng nhập với Solana',
    },
  },
  signInEnterPasswordTitle: 'Nhập mật khẩu',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'Nhập mã xác minh đã gửi đến {{provider}}',
      title: 'Xác minh {{provider}}',
    },
    continue: {
      actionLink: 'Đăng nhập',
      actionText: 'Đã có tài khoản?',
      subtitle: 'Vui lòng điền các chi tiết còn lại để tiếp tục.',
      title: 'Điền các trường còn thiếu',
    },
    emailCode: {
      formSubtitle: 'Nhập mã xác minh đã gửi đến email của bạn',
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'Nhập mã xác minh đã gửi đến email của bạn',
      title: 'Xác minh email',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'Để tiếp tục, mở liên kết xác minh trên thiết bị và trình duyệt từ đó bạn đã khởi động đăng ký',
        title: 'Liên kết xác minh không hợp lệ cho thiết bị này',
      },
      formSubtitle: 'Sử dụng liên kết xác minh đã gửi đến email của bạn',
      formTitle: 'Liên kết xác minh',
      loading: {
        title: 'Đang đăng ký...',
      },
      resendButton: 'Không nhận được liên kết? Gửi lại',
      subtitle: 'để tiếp tục đến {{applicationName}}',
      title: 'Xác minh email',
      verified: {
        title: 'Đăng ký thành công',
      },
      verifiedSwitchTab: {
        subtitle: 'Quay lại tab mới được mở để tiếp tục',
        subtitleNewTab: 'Quay lại tab trước để tiếp tục',
        title: 'Xác minh email thành công',
      },
    },
    enterpriseConnections: {
      subtitle: 'Chọn tài khoản doanh nghiệp bạn muốn dùng để tiếp tục.',
      title: 'Chọn tài khoản doanh nghiệp của bạn',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Tôi đồng ý với {{ privacyPolicyLink || link("Chính sách bảo mật") }}',
        label__onlyTermsOfService: 'Tôi đồng ý với {{ termsOfServiceLink || link("Điều khoản dịch vụ") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'Tôi đồng ý với {{ termsOfServiceLink || link("Điều khoản dịch vụ") }} và {{ privacyPolicyLink || link("Chính sách bảo mật") }}',
      },
      continue: {
        subtitle: 'Vui lòng đọc và đồng ý với các điều khoản để tiếp tục',
        title: 'Đồng ý với điều khoản',
      },
    },
    phoneCode: {
      formSubtitle: 'Nhập mã xác minh đã gửi đến số điện thoại của bạn',
      formTitle: 'Mã xác minh',
      resendButton: 'Không nhận được mã? Gửi lại',
      subtitle: 'Nhập mã xác minh đã gửi đến số điện thoại của bạn',
      title: 'Xác minh điện thoại',
    },
    protectCheck: {
      loading: undefined,
      retryButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    restrictedAccess: {
      actionLink: 'Đăng nhập',
      actionText: 'Đã có tài khoản?',
      blockButton__emailSupport: 'Email hỗ trợ',
      blockButton__joinWaitlist: 'Tham gia danh sách chờ',
      subtitle: 'Đăng ký hiện không khả dụng. Nếu bạn tin rằng bạn có quyền truy cập, vui lòng liên hệ hỗ trợ.',
      subtitleWaitlist:
        'Đăng ký hiện không khả dụng. Để biết thông tin sớm nhất khi chúng tôi khởi chạy, hãy tham gia danh sách chờ.',
      title: 'Quyền truy cập bị giới hạn',
    },
    start: {
      actionLink: 'Đăng nhập',
      actionLink__use_email: 'Sử dụng email thay vì',
      actionLink__use_phone: 'Sử dụng điện thoại thay vì',
      actionText: 'Đã có tài khoản?',
      alternativePhoneCodeProvider: {
        actionLink: 'Sử dụng phương thức khác',
        label: '{{provider}} số điện thoại',
        subtitle: 'Nhập số điện thoại của bạn để nhận mã xác minh trên {{provider}}.',
        title: 'Đăng ký vào {{applicationName}} với {{provider}}',
      },
      subtitle: 'Chào mừng! Vui lòng điền các chi tiết để bắt đầu.',
      subtitleCombined: 'Chào mừng! Vui lòng điền các chi tiết để bắt đầu.',
      title: 'Tạo tài khoản của bạn',
      titleCombined: 'Tạo tài khoản của bạn',
    },
    web3Solana: {
      subtitle: 'Chọn ví bên dưới để đăng ký',
      title: 'Đăng ký với Solana',
    },
  },
  socialButtonsBlockButton: 'Tiếp tục với {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'Một tổ chức đã tồn tại cho tên công ty được phát hiện ({{organizationName}}) và {{organizationDomain}}. Tham gia bằng lời mời.',
    },
    chooseOrganization: {
      action__createOrganization: 'Tạo tổ chức mới',
      action__invitationAccept: 'Tham gia',
      action__suggestionsAccept: 'Yêu cầu tham gia',
      subtitle: 'Tham gia một tổ chức hiện có hoặc tạo mới',
      subtitle__createOrganizationDisabled: 'Tham gia một tổ chức hiện có',
      suggestionsAcceptedLabel: 'Đang chờ phê duyệt',
      title: 'Chọn tổ chức',
    },
    createOrganization: {
      formButtonReset: 'Hủy',
      formButtonSubmit: 'Tiếp tục',
      formFieldInputPlaceholder__name: 'Tổ chức của tôi',
      formFieldInputPlaceholder__slug: 'to-chuc-cua-toi',
      formFieldLabel__name: 'Tên',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Nhập thông tin chi tiết tổ chức của bạn để tiếp tục',
      title: 'Thiết lập tổ chức của bạn',
    },
    organizationCreationDisabled: {
      subtitle: 'Liên hệ với quản trị viên tổ chức của bạn để nhận lời mời.',
      title: 'Bạn phải thuộc về một tổ chức',
    },
    signOut: {
      actionLink: 'Đăng xuất',
      actionText: 'Đã đăng nhập với {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: 'Đặt lại mật khẩu',
    signOut: {
      actionLink: 'Đăng xuất',
      actionText: 'Đã đăng nhập với tư cách {{identifier}}',
    },
    subtitle: 'Tài khoản của bạn yêu cầu mật khẩu mới trước khi bạn có thể tiếp tục',
    title: 'Đặt lại mật khẩu của bạn',
  },
  taskSetupMfa: {
    badge: 'Thiết lập xác minh hai bước',
    signOut: {
      actionLink: 'Đăng xuất',
      actionText: 'Đã đăng nhập với tư cách {{identifier}}',
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: 'Tiếp tục',
        infoText:
          'Một tin nhắn văn bản chứa mã xác minh sẽ được gửi đến số điện thoại này. Có thể áp dụng cước phí tin nhắn và dữ liệu.',
      },
      addPhoneNumber: 'Thêm số điện thoại',
      cancel: 'Hủy',
      subtitle: 'Chọn số điện thoại bạn muốn sử dụng để xác minh hai bước bằng mã SMS',
      success: {
        finishButton: 'Tiếp tục',
        message1:
          'Xác minh hai bước hiện đã được bật. Khi đăng nhập, bạn sẽ cần nhập mã xác minh được gửi đến số điện thoại này như một bước bổ sung.',
        message2:
          'Lưu các mã dự phòng này và cất giữ ở nơi an toàn. Nếu bạn mất quyền truy cập vào thiết bị xác thực, bạn có thể sử dụng mã dự phòng để đăng nhập.',
        title: 'Đã bật xác minh bằng mã SMS',
      },
      title: 'Thêm xác minh bằng mã SMS',
      verifyPhone: {
        formButtonPrimary: 'Tiếp tục',
        formTitle: 'Mã xác minh',
        resendButton: 'Không nhận được mã? Gửi lại',
        subtitle: 'Nhập mã xác minh được gửi đến',
        title: 'Xác minh số điện thoại của bạn',
      },
    },
    start: {
      methodSelection: {
        phoneCode: 'Mã SMS',
        totp: 'Ứng dụng xác thực',
      },
      subtitle: 'Chọn phương thức bạn muốn sử dụng để bảo vệ tài khoản của mình bằng một lớp bảo mật bổ sung',
      title: 'Thiết lập xác minh hai bước',
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Thay vào đó, quét mã QR',
        buttonUnableToScan__nonPrimary: 'Không thể quét mã QR?',
        formButtonPrimary: 'Tiếp tục',
        formButtonReset: 'Hủy',
        infoText__ableToScan:
          'Thiết lập một phương thức đăng nhập mới trong ứng dụng xác thực của bạn và quét mã QR sau đây để liên kết nó với tài khoản của bạn.',
        infoText__unableToScan:
          'Thiết lập một phương thức đăng nhập mới trong ứng dụng xác thực của bạn và nhập Khóa được cung cấp bên dưới.',
        inputLabel__unableToScan1:
          'Đảm bảo rằng Mã dựa trên thời gian hoặc Mã một lần đã được bật, sau đó hoàn tất việc liên kết tài khoản của bạn.',
      },
      success: {
        finishButton: 'Tiếp tục',
        message1:
          'Xác minh hai bước hiện đã được bật. Khi đăng nhập, bạn sẽ cần nhập mã xác minh từ ứng dụng xác thực này như một bước bổ sung.',
        message2:
          'Lưu các mã dự phòng này và cất giữ ở nơi an toàn. Nếu bạn mất quyền truy cập vào thiết bị xác thực, bạn có thể sử dụng mã dự phòng để đăng nhập.',
        title: 'Đã bật xác minh bằng ứng dụng xác thực',
      },
      title: 'Thêm ứng dụng xác thực',
      verifyTotp: {
        formButtonPrimary: 'Tiếp tục',
        formButtonReset: 'Hủy',
        formTitle: 'Mã xác minh',
        subtitle: 'Nhập mã xác minh được tạo bởi ứng dụng xác thực của bạn',
        title: 'Thêm ứng dụng xác thực',
      },
    },
  },
  unstable__errors: {
    action_blocked: undefined,
    already_a_member_in_organization: '{{email}} đã là thành viên của tổ chức.',
    api_key_name_already_exists: undefined,
    api_key_usage_exceeded: undefined,
    avatar_file_size_exceeded: 'Kích thước tệp vượt quá giới hạn tối đa 10MB. Vui lòng chọn tệp nhỏ hơn.',
    avatar_file_type_invalid: 'Loại tệp không được hỗ trợ. Vui lòng tải lên hình ảnh JPG, PNG, GIF hoặc WEBP.',
    captcha_invalid: undefined,
    captcha_unavailable:
      'Đăng ký không thành công do lỗi bot. Vui lòng tải lại trang để thử lại hoặc liên hệ hỗ trợ để được hỗ trợ.',
    form_code_incorrect: undefined,
    form_email_address_blocked:
      'Các dịch vụ email tạm thời không được hỗ trợ. Vui lòng sử dụng địa chỉ email thông thường của bạn để tạo tài khoản.',
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: undefined,
    form_new_password_matches_current: 'Mật khẩu mới không được trùng với mật khẩu hiện tại.',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: undefined,
    form_param_format_invalid__phone_number: undefined,
    form_param_max_length_exceeded__first_name: undefined,
    form_param_max_length_exceeded__last_name: undefined,
    form_param_max_length_exceeded__name: undefined,
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: 'Mật khẩu của bạn quá ngắn. Nó phải có ít nhất 8 ký tự.',
    form_password_not_strong_enough: 'Mật khẩu của bạn không đủ mạnh.',
    form_password_or_identifier_incorrect:
      'Mật khẩu hoặc địa chỉ email không đúng. Vui lòng thử lại hoặc sử dụng phương thức khác.',
    form_password_pwned:
      'Mật khẩu này đã được tìm thấy trong một rò rỉ và không thể được sử dụng, vui lòng thử một mật khẩu khác.',
    form_password_pwned__sign_in:
      'Mật khẩu này đã được tìm thấy trong một rò rỉ và không thể được sử dụng, vui lòng đặt lại mật khẩu của bạn.',
    form_password_size_in_bytes_exceeded: undefined,
    form_password_untrusted__sign_in:
      'Mật khẩu của bạn có thể đã bị xâm phạm. Để bảo vệ tài khoản, vui lòng tiếp tục bằng một phương thức đăng nhập khác. Bạn sẽ được yêu cầu đặt lại mật khẩu sau khi đăng nhập.',
    form_password_validation_failed: undefined,
    form_username_invalid_character: undefined,
    form_username_invalid_length: 'Tên người dùng của bạn phải có giữa {{min_length}} và {{max_length}} ký tự.',
    form_username_needs_non_number_char: 'Tên người dùng của bạn phải chứa ít nhất một ký tự không phải là số.',
    identification_deletion_failed: undefined,
    insufficient_seats_change_plan: undefined,
    insufficient_seats_contact_support: undefined,
    not_allowed_access: undefined,
    oauth_access_denied: undefined,
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded:
      'Bạn đã đạt đến giới hạn {{maxAllowed}} thành viên tổ chức, bao gồm cả các lời mời đang chờ xử lý.',
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized:
      'Bạn không còn là thành viên của tổ chức này. Vui lòng chọn hoặc tạo một tổ chức khác.',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      'Bạn không còn là thành viên của tổ chức này. Vui lòng chọn một tổ chức khác.',
    passkey_already_exists: 'Mã passkey đã được đăng ký với thiết bị này.',
    passkey_not_supported: 'Mã passkey không được hỗ trợ trên thiết bị này.',
    passkey_pa_not_supported: 'Đăng ký yêu cầu một bộ xác thực nền tảng nhưng thiết bị không hỗ trợ nó.',
    passkey_registration_cancelled: 'Đăng ký mã passkey đã bị hủy hoặc hết hạn.',
    passkey_retrieval_cancelled: 'Xác minh mã passkey đã bị hủy hoặc hết hạn.',
    passwordComplexity: {
      maximumLength: 'ít hơn {{length}} ký tự',
      minimumLength: '{{length}} hoặc nhiều hơn ký tự',
      requireLowercase: 'một chữ cái viết thường',
      requireNumbers: 'một số',
      requireSpecialCharacter: 'một ký tự đặc biệt',
      requireUppercase: 'một chữ cái viết hoa',
      sentencePrefix: 'Mật khẩu của bạn phải chứa',
    },
    phone_number_exists: undefined,
    protect_check_aborted: undefined,
    protect_check_already_resolved: undefined,
    protect_check_execution_failed: undefined,
    protect_check_invalid_script: undefined,
    protect_check_invalid_sdk_url: undefined,
    protect_check_script_load_failed: undefined,
    protect_check_timed_out: undefined,
    protect_check_unsupported_environment: undefined,
    session_exists: undefined,
    web3_missing_identifier: 'Không tìm thấy phần mở rộng Web3 Wallet. Vui lòng cài đặt một phần mở rộng để tiếp tục.',
    web3_signature_request_rejected: 'Bạn đã từ chối yêu cầu ký. Vui lòng thử lại để tiếp tục.',
    web3_solana_signature_generation_failed: 'Đã xảy ra lỗi khi tạo chữ ký. Vui lòng thử lại để tiếp tục.',
    zxcvbn: {
      couldBeStronger: 'Mật khẩu của bạn hoạt động, nhưng có thể mạnh hơn. Hãy thử thêm nhiều ký tự.',
      goodPassword: 'Mật khẩu của bạn đáp ứng tất cả các yêu cầu cần thiết.',
      notEnough: 'Mật khẩu của bạn không đủ mạnh.',
      suggestions: {
        allUppercase: 'Viết hoa một số chữ cái, nhưng không phải tất cả.',
        anotherWord: 'Thêm nhiều từ khác nhau hơn.',
        associatedYears: 'Tránh năm mà bạn liên quan đến.',
        capitalization: 'Viết hoa nhiều hơn chữ cái đầu tiên.',
        dates: 'Tránh ngày và năm mà bạn liên quan đến.',
        l33t: "Tránh các thay thế chữ cái dễ dự đoán như '@' thay vì 'a'.",
        longerKeyboardPattern: 'Sử dụng các khuôn mẫu bàn phím dài hơn và thay đổi hướng gõ nhiều lần.',
        noNeed: 'Bạn có thể tạo mật khẩu mạnh mà không sử dụng các ký tự đặc biệt, số hoặc chữ cái viết hoa.',
        pwned: 'Nếu bạn sử dụng mật khẩu này ở nơi khác, bạn nên thay đổi nó.',
        recentYears: 'Tránh năm gần đây.',
        repeated: 'Tránh các từ và ký tự lặp lại.',
        reverseWords: 'Tránh các từ viết ngược của các từ thông dụng.',
        sequences: 'Tránh các chuỗi ký tự thông dụng.',
        useWords: 'Sử dụng nhiều từ, nhưng tránh các cụm từ thông dụng.',
      },
      warnings: {
        common: 'Đây là một mật khẩu thường được sử dụng.',
        commonNames: 'Tên và họ thường được sử dụng.',
        dates: 'Ngày thường được sử dụng.',
        extendedRepeat: 'Các mẫu ký tự lặp lại như "abcabcabc" dễ dự đoán.',
        keyPattern: 'Các mẫu bàn phím ngắn dễ dự đoán.',
        namesByThemselves: 'Tên hoặc họ dễ dự đoán.',
        pwned: 'Mật khẩu của bạn đã bị rò rỉ qua một rò rỉ dữ liệu trên Internet.',
        recentYears: 'Năm gần đây dễ dự đoán.',
        sequences: 'Các chuỗi ký tự thông dụng như "abc" dễ dự đoán.',
        similarToCommon: 'Đây là tương tự như một mật khẩu thường được sử dụng.',
        simpleRepeat: 'Các ký tự lặp lại như "aaa" dễ dự đoán.',
        straightRow: 'Các hàng ký tự trên bàn phím của bạn dễ dự đoán.',
        topHundred: 'Đây là một mật khẩu thường được sử dụng.',
        topTen: 'Đây là một mật khẩu thường được sử dụng.',
        userInputs: 'Không nên có bất kỳ dữ liệu cá nhân hoặc liên quan đến trang nào.',
        wordByItself: 'Các từ đơn dễ dự đoán.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Thêm tài khoản',
    action__closeUserMenu: 'Đóng menu người dùng',
    action__manageAccount: 'Quản lý tài khoản',
    action__openUserMenu: 'Mở menu người dùng',
    action__signOut: 'Đăng xuất',
    action__signOutAll: 'Đăng xuất tất cả tài khoản',
    label__accountActions: 'Hành động tài khoản',
    label__activeSessions: 'Phiên hoạt động',
    label__userButtonPopover: 'Bảng điều khiển tài khoản',
  },
  userProfile: {
    apiKeysPage: {
      title: 'Khoá API',
    },
    backupCodePage: {
      actionLabel__copied: 'Đã sao chép!',
      actionLabel__copy: 'Sao chép tất cả',
      actionLabel__download: 'Tải xuống .txt',
      actionLabel__print: 'In',
      infoText1: 'Mã sao lưu sẽ được bật cho tài khoản này.',
      infoText2:
        'Giữ mã sao lưu bí mật và lưu trữ chúng một cách an toàn. Bạn có thể tạo lại mã sao lưu nếu bạn nghi ngờ chúng đã bị rò rỉ.',
      subtitle__codelist: 'Lưu trữ chúng một cách an toàn và giữ bí mật.',
      successMessage:
        'Mã sao lưu đã được bật. Bạn có thể sử dụng một trong số chúng để đăng nhập vào tài khoản của bạn, nếu bạn mất quyền truy cập vào thiết bị xác thực của bạn. Mỗi mã chỉ có thể được sử dụng một lần.',
      successSubtitle:
        'Bạn có thể sử dụng một trong số chúng để đăng nhập vào tài khoản của bạn, nếu bạn mất quyền truy cập vào thiết bị xác thực của bạn.',
      title: 'Thêm xác minh mã sao lưu',
      title__codelist: 'Mã sao lưu',
    },
    billingPage: {
      accountCreditsSection: {
        title: undefined,
        viewHistory: undefined,
      },
      creditHistoryPage: {
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
      },
      paymentHistorySection: {
        empty: 'Không có lịch sử thanh toán',
        notFound: 'Không tìm thấy lịch sử thanh toán',
        tableHeader__amount: 'Số tiền',
        tableHeader__date: 'Ngày',
        tableHeader__status: 'Trạng thái',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Làm mặc định',
        actionLabel__remove: 'Xóa',
        add: 'Thêm phương thức thanh toán mới',
        addSubtitle: 'Thêm một phương thức thanh toán mới vào tài khoản của bạn.',
        cancelButton: 'Hủy',
        formButtonPrimary__add: 'Thêm phương thức thanh toán',
        formButtonPrimary__pay: 'Thanh toán {{amount}}',
        payWithTestCardButton: 'Thanh toán với thẻ thử',
        removeMethod: {
          messageLine1: '{{identifier}} sẽ bị xóa khỏi tài khoản này.',
          messageLine2:
            'Bạn sẽ không còn thể sử dụng nguồn thanh toán này và bất kỳ đăng ký lặp lại nào phụ thuộc vào nó sẽ không còn hoạt động.',
          successMessage: '{{paymentMethod}} đã bị xóa khỏi tài khoản của bạn.',
          title: 'Xóa phương thức thanh toán',
        },
        title: 'Phương thức thanh toán',
      },
      start: {
        headerTitle__payments: 'Thanh toán',
        headerTitle__plans: 'Gói',
        headerTitle__statements: 'Báo cáo',
        headerTitle__subscriptions: 'Đăng ký',
      },
      statementsSection: {
        empty: 'Không có báo cáo để hiển thị',
        itemCaption__paidForPlan: 'Thanh toán cho {{plan}} {{period}} gói',
        itemCaption__payerCredit: undefined,
        itemCaption__proratedCredit: 'Tín dụng phân chia cho sử dụng một phần của đăng ký trước',
        itemCaption__subscribedAndPaidForPlan: 'Đăng ký và thanh toán cho {{plan}} {{period}} gói',
        notFound: 'Không tìm thấy báo cáo',
        tableHeader__amount: 'Số tiền',
        tableHeader__date: 'Ngày',
        title: 'Báo cáo',
        totalPaid: 'Tổng thanh toán',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Quản lý',
        actionLabel__newSubscription: 'Đăng ký gói',
        actionLabel__switchPlan: 'Chuyển gói',
        overview: undefined,
        tableHeader__edit: 'Sửa',
        tableHeader__plan: 'Gói',
        tableHeader__startDate: 'Ngày bắt đầu',
        title: 'Đăng ký',
      },
      subscriptionsSection: {
        actionLabel__default: 'Quản lý',
      },
      switchPlansSection: {
        title: 'Chuyển gói',
      },
      title: 'Thanh toán',
    },
    connectedAccountPage: {
      formHint: 'Chọn nhà cung cấp để kết nối tài khoản của bạn.',
      formHint__noAccounts: 'Không có nhà cung cấp tài khoản bên ngoài.',
      removeResource: {
        messageLine1: '{{identifier}} sẽ bị xóa khỏi tài khoản này.',
        messageLine2:
          'Bạn sẽ không còn thể sử dụng tài khoản kết nối này và bất kỳ tính năng phụ thuộc nào sẽ không còn hoạt động.',
        successMessage: '{{connectedAccount}} đã bị xóa khỏi tài khoản của bạn.',
        title: 'Xóa tài khoản kết nối',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'Nhà cung cấp đã được thêm vào tài khoản của bạn',
      title: 'Thêm tài khoản kết nối',
    },
    deletePage: {
      actionDescription: 'Nhập "Xóa tài khoản" dưới đây để tiếp tục.',
      confirm: 'Xóa tài khoản',
      messageLine1:
        'Bạn có chắc chắn muốn xóa tài khoản của mình không? Một số dữ liệu liên quan có thể được giữ lại. Để yêu cầu xóa dữ liệu hoàn toàn, vui lòng liên hệ bộ phận hỗ trợ.',
      messageLine2: 'Hành động này là vĩnh viễn và không thể hoàn tác.',
      title: 'Xóa tài khoản',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Một email chứa mã xác thực sẽ được gửi đến địa chỉ email này.',
        formSubtitle: 'Nhập mã xác thực được gửi đến {{identifier}}',
        formTitle: 'Mã xác thực',
        resendButton: 'Không nhận được mã? Gửi lại',
        successMessage: 'Email {{identifier}} đã được thêm vào tài khoản của bạn.',
      },
      emailLink: {
        formHint: 'Một email chứa liên kết xác thực sẽ được gửi đến địa chỉ email này.',
        formSubtitle: 'Nhấp vào liên kết xác thực trong email được gửi đến {{identifier}}',
        formTitle: 'Liên kết xác thực',
        resendButton: 'Không nhận được liên kết? Gửi lại',
        successMessage: 'Email {{identifier}} đã được thêm vào tài khoản của bạn.',
      },
      enterpriseSSOLink: {
        formButton: 'Nhấp để đăng nhập',
        formSubtitle: 'Hoàn thành đăng nhập với {{identifier}}',
      },
      formHint: 'Bạn cần xác thực địa chỉ email này trước khi nó có thể được thêm vào tài khoản của bạn.',
      removeResource: {
        messageLine1: '{{identifier}} sẽ bị xóa khỏi tài khoản này.',
        messageLine2: 'Bạn sẽ không còn thể đăng nhập bằng địa chỉ email này.',
        successMessage: '{{emailAddress}} đã bị xóa khỏi tài khoản của bạn.',
        title: 'Xóa địa chỉ email',
      },
      title: 'Thêm địa chỉ email',
      verifyTitle: 'Xác thực địa chỉ email',
    },
    formButtonPrimary__add: 'Thêm',
    formButtonPrimary__continue: 'Tiếp tục',
    formButtonPrimary__finish: 'Hoàn thành',
    formButtonPrimary__remove: 'Xóa',
    formButtonPrimary__save: 'Lưu',
    formButtonReset: 'Hủy',
    mfaPage: {
      formHint: 'Chọn phương thức để thêm.',
      title: 'Thêm xác thực hai bước',
    },
    mfaPhoneCodePage: {
      backButton: 'Sử dụng số điện thoại hiện có',
      primaryButton__addPhoneNumber: 'Thêm số điện thoại',
      removeResource: {
        messageLine1: '{{identifier}} sẽ không còn nhận được mã xác thực khi đăng nhập.',
        messageLine2: 'Tài khoản của bạn có thể không an toàn hơn. Bạn có chắc chắn muốn tiếp tục?',
        successMessage: 'Xác thực mã SMS hai bước đã bị xóa cho {{mfaPhoneCode}}',
        title: 'Xóa xác thực hai bước',
      },
      subtitle__availablePhoneNumbers:
        'Chọn số điện thoại hiện có để đăng ký xác thực mã SMS hai bước hoặc thêm số điện thoại mới.',
      subtitle__unavailablePhoneNumbers:
        'Không có số điện thoại nào để đăng ký xác thực mã SMS hai bước, vui lòng thêm số điện thoại mới.',
      successMessage1: 'Khi đăng nhập, bạn sẽ cần nhập mã xác thực được gửi đến số điện thoại này làm bước thêm.',
      successMessage2:
        'Lưu các mã sao lưu này và lưu trữ chúng ở một nơi an toàn. Nếu bạn mất quyền truy cập vào thiết bị xác thực của bạn, bạn có thể sử dụng mã sao lưu để đăng nhập.',
      successTitle: 'Xác thực mã SMS đã được bật',
      title: 'Thêm xác thực mã SMS',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Quét mã QR thay vì',
        buttonUnableToScan__nonPrimary: 'Không thể quét mã QR?',
        infoText__ableToScan:
          'Thiết lập phương thức đăng nhập mới trong ứng dụng xác thực của bạn và quét mã QR sau để kết nối với tài khoản của bạn.',
        infoText__unableToScan:
          'Thiết lập phương thức đăng nhập mới trong ứng dụng xác thực của bạn và nhập khóa được cung cấp dưới đây.',
        inputLabel__unableToScan1:
          'Đảm bảo mã thời gian hoặc mã một lần đã được bật, sau đó hoàn thành việc kết nối tài khoản của bạn.',
        inputLabel__unableToScan2:
          'Ngoài ra, nếu ứng dụng xác thực của bạn hỗ trợ TOTP URIs, bạn cũng có thể sao chép URI đầy đủ.',
      },
      removeResource: {
        messageLine1: 'Mã xác thực từ ứng dụng xác thực này sẽ không còn được yêu cầu khi đăng nhập.',
        messageLine2: 'Tài khoản của bạn có thể không an toàn hơn. Bạn có chắc chắn muốn tiếp tục?',
        successMessage: 'Xác thực hai bước qua ứng dụng xác thực đã bị xóa.',
        title: 'Xóa xác thực hai bước',
      },
      successMessage:
        'Xác thực hai bước đã được bật. Khi đăng nhập, bạn sẽ cần nhập mã xác thực từ ứng dụng xác thực này làm bước thêm.',
      title: 'Thêm ứng dụng xác thực',
      verifySubtitle: 'Nhập mã xác thực được tạo bởi ứng dụng xác thực của bạn',
      verifyTitle: 'Mã xác thực',
    },
    mobileButton__menu: 'Menu',
    navbar: {
      account: 'Tài khoản',
      apiKeys: 'Khoá API',
      billing: 'Thanh toán',
      description: 'Quản lý thông tin tài khoản của bạn.',
      security: 'Bảo mật',
      title: 'Tài khoản',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} sẽ bị xóa khỏi tài khoản này.',
        title: 'Xóa passkey',
      },
      subtitle__rename: 'Bạn có thể thay đổi tên passkey để dễ dàng tìm kiếm.',
      title__rename: 'Đổi tên Passkey',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Được khuyến nghị đăng xuất khỏi tất cả các thiết bị khác có thể đã sử dụng mật khẩu cũ của bạn.',
      readonly:
        'Mật khẩu của bạn hiện không thể được chỉnh sửa vì bạn chỉ có thể đăng nhập thông qua kết nối doanh nghiệp.',
      successMessage__set: 'Mật khẩu của bạn đã được thiết lập.',
      successMessage__signOutOfOtherSessions: 'Tất cả các thiết bị khác đã được đăng xuất.',
      successMessage__update: 'Mật khẩu của bạn đã được cập nhật.',
      title__set: 'Thiết lập mật khẩu',
      title__update: 'Cập nhật mật khẩu',
    },
    phoneNumberPage: {
      infoText:
        'Một tin nhắn văn bản chứa mã xác thực sẽ được gửi đến số điện thoại này. Có thể áp dụng tỷ lệ phí tin nhắn và dữ liệu.',
      removeResource: {
        messageLine1: '{{identifier}} sẽ bị xóa khỏi tài khoản này.',
        messageLine2: 'Bạn sẽ không còn thể đăng nhập bằng số điện thoại này.',
        successMessage: '{{phoneNumber}} đã bị xóa khỏi tài khoản của bạn.',
        title: 'Xóa số điện thoại',
      },
      successMessage: '{{identifier}} đã được thêm vào tài khoản của bạn.',
      title: 'Thêm số điện thoại',
      verifySubtitle: 'Nhập mã xác thực được gửi đến {{identifier}}',
      verifyTitle: 'Xác thực số điện thoại',
    },
    plansPage: {
      title: 'Gói',
    },
    profilePage: {
      fileDropAreaHint: 'Kích thước khuyến nghị 1:1, tối đa 10MB.',
      imageFormDestructiveActionSubtitle: 'Xóa',
      imageFormSubtitle: 'Tải lên',
      imageFormTitle: 'Ảnh hồ sơ',
      readonly: 'Thông tin hồ sơ của bạn đã được cung cấp thông qua kết nối doanh nghiệp và không thể được chỉnh sửa.',
      successMessage: 'Hồ sơ của bạn đã được cập nhật.',
      title: 'Cập nhật hồ sơ',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Đăng xuất khỏi thiết bị',
        title: 'Thiết bị hoạt động',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Kết nối lại',
        actionLabel__reauthorize: 'Xác thực ngay',
        destructiveActionTitle: 'Xóa',
        primaryButton: 'Kết nối tài khoản',
        subtitle__disconnected: 'Tài khoản này đã bị ngắt kết nối.',
        subtitle__reauthorize:
          'Các phạm vi được yêu cầu đã được cập nhật, và bạn có thể đang gặp phải chức năng giới hạn. Vui lòng xác thực lại ứng dụng này để tránh bất kỳ vấn đề nào',
        title: 'Tài khoản kết nối',
      },
      dangerSection: {
        deleteAccountButton: 'Xóa tài khoản',
        title: 'Xóa tài khoản',
      },
      emailAddressesSection: {
        destructiveAction: 'Xóa email',
        detailsAction__nonPrimary: 'Đặt làm chính',
        detailsAction__primary: 'Hoàn thành xác thực',
        detailsAction__unverified: 'Xác thực',
        primaryButton: 'Thêm địa chỉ email',
        title: 'Địa chỉ email',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Kết nối tài khoản',
        title: 'Tài khoản doanh nghiệp',
      },
      headerTitle__account: 'Chi tiết hồ sơ',
      headerTitle__security: 'Bảo mật',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Tạo lại',
          headerTitle: 'Mã sao lưu',
          subtitle__regenerate:
            'Lấy một bộ mã sao lưu mới và an toàn. Các mã sao lưu trước đó sẽ bị xóa và không thể được sử dụng.',
          title__regenerate: 'Tạo lại mã sao lưu',
        },
        phoneCode: {
          actionLabel__setDefault: 'Đặt làm chính',
          destructiveActionLabel: 'Xóa',
        },
        primaryButton: 'Thêm xác thực hai bước',
        title: 'Xác thực hai bước',
        totp: {
          destructiveActionTitle: 'Xóa',
          headerTitle: 'Ứng dụng xác thực',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Xóa',
        menuAction__rename: 'Đổi tên',
        primaryButton: 'Thêm passkey',
        title: 'Passkeys',
      },
      passwordSection: {
        primaryButton__setPassword: 'Thiết lập mật khẩu',
        primaryButton__updatePassword: 'Cập nhật mật khẩu',
        title: 'Mật khẩu',
      },
      phoneNumbersSection: {
        destructiveAction: 'Xóa số điện thoại',
        detailsAction__nonPrimary: 'Đặt làm chính',
        detailsAction__primary: 'Hoàn thành xác thực',
        detailsAction__unverified: 'Xác thực số điện thoại',
        primaryButton: 'Thêm số điện thoại',
        title: 'Số điện thoại',
      },
      profileSection: {
        primaryButton: 'Cập nhật hồ sơ',
        title: 'Hồ sơ',
      },
      usernameSection: {
        primaryButton__setUsername: 'Thiết lập tên người dùng',
        primaryButton__updateUsername: 'Cập nhật tên người dùng',
        title: 'Tên người dùng',
      },
      web3WalletsSection: {
        destructiveAction: 'Xóa ví',
        detailsAction__nonPrimary: 'Đặt làm chính',
        primaryButton: 'Kết nối ví',
        title: 'Ví Web3',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Chọn một ví Solana để kết nối với tài khoản của bạn.',
          title: 'Thêm ví Solana',
        },
      },
    },
    usernamePage: {
      successMessage: 'Tên người dùng của bạn đã được cập nhật.',
      title__set: 'Thiết lập tên người dùng',
      title__update: 'Cập nhật tên người dùng',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} sẽ bị xóa khỏi tài khoản này.',
        messageLine2: 'Bạn sẽ không còn thể đăng nhập bằng ví web3 này.',
        successMessage: '{{web3Wallet}} đã bị xóa khỏi tài khoản của bạn.',
        title: 'Xóa ví web3',
      },
      subtitle__availableWallets: 'Chọn một ví web3 để kết nối với tài khoản của bạn.',
      subtitle__unavailableWallets: 'Không có ví web3 nào có sẵn.',
      successMessage: 'Ví đã được thêm vào tài khoản của bạn.',
      title: 'Thêm ví web3',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Đăng nhập',
      actionText: 'Đã có quyền truy cập?',
      formButton: 'Tham gia danh sách chờ',
      subtitle: 'Nhập địa chỉ email của bạn và chúng tôi sẽ thông báo khi vị trí của bạn đã sẵn sàng',
      title: 'Tham gia danh sách chờ',
    },
    success: {
      message: 'Bạn sẽ được chuyển hướng sớm...',
      subtitle: 'Chúng tôi sẽ liên hệ khi vị trí của bạn đã sẵn sàng',
      title: 'Cảm ơn bạn đã tham gia danh sách chờ!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Kết nối với {{walletName}}',
    continue: 'Tiếp tục với {{walletName}}',
    noneAvailable:
      'Không phát hiện ví Solana Web3 nào. Vui lòng cài đặt {{ solanaWalletsLink || link("wallet extension") }} hỗ trợ Web3.',
  },
} as const;
